import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Button,
  Grid,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useState } from 'react';
import DetailsModal from '@/components/DetailsModal';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import validations from '@/helpers/validation';
import { MonthPickerInput } from '@mantine/dates';
import { YES_NO } from '@/helpers/enums';
import agencyCommissionsMutation from '@/api/mutitations/agencyCommissions';
import s3Mutations from '@/api/mutitations/s3';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';
import TieredCommissionInput from '../TieredCommissionInput';

function AgencyCommissionsCreate(props) {
  const {
    open,
    close,
    opened,
    setKey,
  } = props;

  const t = prepareTranslate();
  const { useUpload } = s3Mutations;
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      agencyFeeMonthlyDeposit: 0,
      agencyFeeMonthlyAccumulation: 0,
      agencyFeeAnnualDeposit: [{ from: 0, to: null, rate: 0 }],
      agencyFeeAnnualAccumulation: [{ from: 0, to: null, rate: 0 }],
      files: [],
      comments: '',
    },
    validate: {
      company: (value) => validations.required(value),
      product: (value) => validations.required(value),
      payeeFeesStartDate: (value) => validations.required(value),
      payeeFeesValidityDate: (value, values) => {
        if (!value || !values.payeeFeesStartDate) return null;
        return value > values.payeeFeesStartDate ? null : 'תאריך תוקף חייב להיות אחרי תאריך ההתחלה';
      },
      volumeFeesStartDate: (value) => validations.required(value),
      volumeFeesValidityDate: (value, values) => {
        if (!value || !values.volumeFeesStartDate) return null;
        return value > values.volumeFeesStartDate ? null : 'תאריך תוקף חייב להיות אחרי תאריך ההתחלה';
      },
      agencyFeeAnnualDeposit: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
      agencyFeeAnnualAccumulation: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
    },
  });

  const companiesOptions = companiesData ? (
    companiesData.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  const productsOptions = productsData?.map((product) => ({
    label: product.hebrew,
    value: product._id,
  }));

  const { mutateAsync: agencyCommissionsMutate } = agencyCommissionsMutation.useCreateOne();

  const handleAgencyCommissionaSave = async (values) => {
    setLoading(true);
    let uploadFiles;

    try {
      const clonedValues = { ...values };
      const { files } = values;
      if (files.length > 0) {
        const formData = new FormData();

        files.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          uploadFiles = await uploadMutate(formData);
          clonedValues.files = uploadFiles.files;
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPayeeFeesVatIncluded = clonedValues.isPayeeFeesVatIncluded === 'yes';
      clonedValues.isVolumeFeesVatIncluded = clonedValues.isVolumeFeesVatIncluded === 'yes';
      clonedValues.status = 'active';

      const newAgencyCommissions = await agencyCommissionsMutate(clonedValues);
      if (!newAgencyCommissions) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('pages.agencyCommissionsCreate.notification.success.title'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.agentEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleAgencyCommissionaSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='clseButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  const payeeFeesMinDate = form.values.payeeFeesStartDate
    ? new Date(
      form.values.payeeFeesStartDate.getFullYear(),
      form.values.payeeFeesStartDate.getMonth() + 1,
      1,
    ) : undefined;

  const volumeFeesMinDate = form.values.volumeFeesStartDate
    ? new Date(
      form.values.volumeFeesStartDate.getFullYear(),
      form.values.volumeFeesStartDate.getMonth() + 1,
      1,
    ) : undefined;

  return (
    <>
      <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened = {opened}
        label={t('pages.agencyCommissionsCreate.title')}
        size='90%'
      >
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.company')}
              data={companiesOptions}
              required
              key={form.key('company')}
              {...form.getInputProps('company')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MultiSelect
              label={t('pages.agencyCommissionsCreate.form.product')}
              data={productsOptions}
              required
              key={form.key('product')}
              {...form.getInputProps('product')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <TextInput
              label={t('pages.agencyCommissionsCreate.form.agentNumber')}
              key={form.key('agentNumber')}
              {...form.getInputProps('agentNumber')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.payeeFeesStartDate')}
              key={form.key('payeeFeesStartDate')}
              required
              {...form.getInputProps('payeeFeesStartDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.payeeFeesValidityDate')}
              key={form.key('payeeFeesValidityDate')}
              minDate={payeeFeesMinDate}
              {...form.getInputProps('payeeFeesValidityDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <NumberInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeMonthlyDeposit')}
              key={form.key('agencyFeeMonthlyDeposit')}
              maxLength={5}
              min={0}
              defaultValue={0}
              {...form.getInputProps('agencyFeeMonthlyDeposit')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <NumberInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeMonthlyAccumulation')}
              key={form.key('agencyFeeMonthlyAccumulation')}
              maxLength={5}
              min={0}
              defaultValue={0}
              {...form.getInputProps('agencyFeeMonthlyAccumulation')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.isPayeeFeesVatIncluded')}
              data={YES_NO}
              key={form.key('isPayeeFeesVatIncluded')}
              {...form.getInputProps('isPayeeFeesVatIncluded')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.volumeFeesStartDate')}
              key={form.key('volumeFeesStartDate')}
              required
              {...form.getInputProps('volumeFeesStartDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.volumeFeesValidityDate')}
              key={form.key('volumeFeesValidityDate')}
              minDate={volumeFeesMinDate}
              {...form.getInputProps('volumeFeesValidityDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.isVolumeFeesVatIncluded')}
              data={YES_NO}
              key={form.key('isVolumeFeesVatIncluded')}
              {...form.getInputProps('isVolumeFeesVatIncluded')}
            />
          </Grid.Col>
        </Grid>

        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualDeposit')}
              {...form.getInputProps('agencyFeeAnnualDeposit')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualAccumulation')}
              {...form.getInputProps('agencyFeeAnnualAccumulation')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Textarea
              label={t('pages.agencyCommissionsCreate.form.comments')}
              key={form.key('comments')}
              minRows={3}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
        </Grid>

        <UploadFilesButton
          onFilesSelected={onFileSelected}
          label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
        />

      {form.values.files?.length > 0 && (
        <EditFiles
        files={form.values.files}
        onNameChange={onFileNameChange}
        onRemove={onDeleteFile}
       />
      )}
      </DetailsModal>

      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

AgencyCommissionsCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
};

export default AgencyCommissionsCreate;
